import {uuid} from '../../../../components/utils/uuid';
import type {CharacterState, Coin, EquipmentEntry} from './character-state';

/** откуда стартовое снаряжение: стартовый класс или предыстория */
export type StartingSource = 'class' | 'origin';

/** вид предмета, из которого выбирают «музыкальный инструмент по вашему выбору» */
export type PickCategory = 'instrument' | 'gamingSet' | 'artisansTools';

export type StartingEntry =
	| {kind: 'item'; itemId: string; quantity: number}
	/** выбор предмета: из перечисленных или любого нужного вида */
	| {
			kind: 'pick';
			key: string;
			label: string;
			quantity: number;
			itemIds?: string[];
			categories: PickCategory[];
			/** «выбранный выше»: по умолчанию берётся инструмент, которым персонаж владеет */
			fromProficiency: boolean;
	  }
	/** предмет без ссылки: ищется по названию, иначе попадает в «Другие вещи» */
	| {kind: 'text'; text: string};

export type StartingOption = {
	/** буква варианта: А, Б, В */
	key: string;
	entries: StartingEntry[];
	coins: Partial<Record<Coin, number>>;
};

/** что уже добавлено в лист выбранным вариантом: при смене варианта это убирается */
export type StartingSelection = {
	option: string;
	/** ключ выбора в варианте → id предмета */
	picks: Record<string, string>;
	coins: Partial<Record<Coin, number>>;
	otherItems: string[];
};

/** предметы варианта после выбора: что положить в снаряжение и в «Другие вещи» */
export type ResolvedStartingOption = {
	/** separate — каждый предмет отдельной записью: доспехи и щиты надевают по одному */
	items: {itemId: string; quantity: number; separate: boolean}[];
	otherItems: string[];
	coins: Partial<Record<Coin, number>>;
};

const COIN_BY_LABEL: Record<string, Coin> = {ЗМ: 'gp', СМ: 'sp', ММ: 'cp', ЭМ: 'ep', ПМ: 'pp'};
const LATIN_LETTERS: Record<string, string> = {A: 'А', B: 'Б', C: 'В', D: 'Г'};
const OPTION_MARK = /\(([АБВГABCD])\)/g;
const ITEM_TAG = /\{@item\s+([^,}]+)[^}]*\}/g;
const ITEM_PART = /^(?:(\d+)\s+)?\{@item\s+([^,}]+),[^}]*\}\s*(?:\((.*)\))?$/;
const COIN_PART = /^(\d+)\s*(ЗМ|СМ|ММ|ЭМ|ПМ)$/;

const CATEGORY_PATTERNS: [RegExp, PickCategory][] = [
	[/музыкальн/i, 'instrument'],
	[/игров|настольн/i, 'gamingSet'],
	[/ремесленник/i, 'artisansTools'],
];

/** «(А) …; (Б) …» — варианты снаряжения из текста класса или предыстории */
export function parseStartingEquipment(source: string | undefined): StartingOption[] {
	if (!source) return [];
	const text = source.replace(/ /g, ' ');
	const marks = [...text.matchAll(OPTION_MARK)];
	return marks.map((mark, index) => {
		const start = (mark.index ?? 0) + mark[0].length;
		const end = marks[index + 1]?.index ?? text.length;
		const key = LATIN_LETTERS[mark[1]] || mark[1];
		return parseOption(key, text.slice(start, end));
	});
}

function parseOption(key: string, text: string): StartingOption {
	const option: StartingOption = {key, entries: [], coins: {}};
	splitParts(text).forEach((part, index) => {
		const coins = part.match(COIN_PART);
		if (coins) {
			const coin = COIN_BY_LABEL[coins[2]];
			option.coins[coin] = (option.coins[coin] || 0) + Number(coins[1]);
			return;
		}
		option.entries.push(parseEntry(part, `${key}-${index}`));
	});
	return option;
}

function parseEntry(part: string, key: string): StartingEntry {
	const item = part.match(ITEM_PART);
	if (item) {
		const [, count, itemId, note = ''] = item;
		const noteIds = [...note.matchAll(ITEM_TAG)].map((tag) => tag[1].trim());
		const noteQuantity = Number(note.match(/^(?:на\s+)?(\d+)/)?.[1]) || 1;
		const quantity = count ? Number(count) : noteQuantity;
		if (noteIds.length > 1) {
			return {kind: 'pick', key, label: part, quantity, itemIds: noteIds, categories: [], fromProficiency: false};
		}
		return {kind: 'item', itemId: noteIds[0] || itemId.trim(), quantity};
	}
	const categories = CATEGORY_PATTERNS.filter(([pattern]) => pattern.test(part)).map(([, category]) => category);
	if (categories.length && /выбран|выше|по вашему выбору|любой|любая/i.test(part)) {
		return {kind: 'pick', key, label: part, quantity: 1, categories, fromProficiency: /выбран|выше/i.test(part)};
	}
	return {kind: 'text', text: part};
}

/** части варианта через запятую или «и»: запятые внутри ссылок и скобок не делят */
function splitParts(text: string): string[] {
	const parts: string[] = [];
	let depth = 0;
	let current = '';
	for (let index = 0; index < text.length; index++) {
		const char = text[index];
		if (char === '{' || char === '(') depth++;
		if (char === '}' || char === ')') depth--;
		if (depth === 0 && char === ',') {
			parts.push(current);
			current = '';
			continue;
		}
		if (depth === 0 && text.startsWith(' и ', index)) {
			parts.push(current);
			current = '';
			index += 2;
			continue;
		}
		current += char;
	}
	parts.push(current);
	return parts
		.map((part) =>
			part
				.replace(/[;.\s_]+$/, '')
				.replace(/^[\s_]+/, '')
				.trim(),
		)
		.filter(Boolean);
}

/**
 * Выбранный вариант в листе: прежние предметы, монеты и строки этого источника убираются,
 * новые добавляются. Так вариант можно менять сколько угодно раз.
 */
export function applyStartingEquipment(
	state: CharacterState,
	source: StartingSource,
	selection: Omit<StartingSelection, 'coins' | 'otherItems'> | undefined,
	resolved: ResolvedStartingOption | undefined,
): Partial<CharacterState> {
	const previous = state.startingEquipment?.[source];
	const coins = {...state.coins};
	for (const [coin, amount] of Object.entries(previous?.coins || {}) as [Coin, number][]) {
		coins[coin] = Math.max(0, (coins[coin] || 0) - amount);
	}
	for (const [coin, amount] of Object.entries(resolved?.coins || {}) as [Coin, number][]) {
		coins[coin] = (coins[coin] || 0) + amount;
	}

	const otherItems = [...state.otherItems];
	for (const text of previous?.otherItems || []) {
		const index = otherItems.indexOf(text);
		if (index !== -1) otherItems.splice(index, 1);
	}
	otherItems.push(...(resolved?.otherItems || []));

	const added: EquipmentEntry[] = (resolved?.items || []).flatMap(({itemId, quantity, separate}) =>
		(separate ? Array.from({length: quantity}, () => 1) : [quantity]).map((count) => ({
			key: uuid(),
			itemId,
			quantity: count,
			startingSource: source,
		})),
	);
	const {[source]: _removed, ...otherSources} = state.startingEquipment || {};

	return {
		coins,
		otherItems,
		equipment: [...state.equipment.filter((entry) => entry.startingSource !== source), ...added],
		startingEquipment:
			selection && resolved
				? {...otherSources, [source]: {...selection, coins: resolved.coins, otherItems: resolved.otherItems}}
				: otherSources,
	};
}

/** предмет из базы, достаточно для стартового снаряжения */
export type StartingItemInfo = {id: string; title: string; categories: PickCategory[]; separate: boolean};

const normalizeTitle = (title: string) => title.toLowerCase().replace(/ё/g, 'е').trim();

/** из чего выбирать: перечисленные предметы или все предметы нужных видов */
export const pickOptions = (entry: Extract<StartingEntry, {kind: 'pick'}>, items: StartingItemInfo[]) =>
	entry.itemIds
		? items.filter((item) => entry.itemIds!.includes(item.id))
		: items.filter((item) => item.categories.some((category) => entry.categories.includes(category)));

/** «выбранный выше»: инструмент нужного вида, которым персонаж уже владеет */
export const proficientPick = (
	entry: Extract<StartingEntry, {kind: 'pick'}>,
	items: StartingItemInfo[],
	proficiencies: string[],
) =>
	entry.fromProficiency ? pickOptions(entry, items).find((item) => proficiencies.includes(item.id))?.id : undefined;

/** вариант в предметы: текст ищется по названию, невыбранное и ненайденное уходит в «Другие вещи» */
export function resolveStartingOption(
	option: StartingOption,
	picks: Record<string, string>,
	items: StartingItemInfo[],
): ResolvedStartingOption {
	const byTitle = new Map(items.map((item) => [normalizeTitle(item.title), item.id]));
	const separate = new Set(items.filter((item) => item.separate).map((item) => item.id));
	const resolved: ResolvedStartingOption = {items: [], otherItems: [], coins: option.coins};
	const add = (itemId: string, quantity: number) =>
		resolved.items.push({itemId, quantity, separate: separate.has(itemId)});
	for (const entry of option.entries) {
		if (entry.kind === 'item') add(entry.itemId, entry.quantity);
		else if (entry.kind === 'pick') {
			const itemId = picks[entry.key];
			if (itemId) add(itemId, entry.quantity);
			else resolved.otherItems.push(entry.label.replace(/\{@item\s+[^,}]+,\s*([^}]*)\}/g, '$1'));
		} else {
			const itemId = byTitle.get(normalizeTitle(entry.text));
			if (itemId) add(itemId, 1);
			else resolved.otherItems.push(entry.text);
		}
	}
	return resolved;
}
